import { FileDoneOutlined } from '@ant-design/icons';
import { Alert, Tabs } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageHeader from '../components/PageHeader';
import EInvoicePreviewTab from '../components/eInvoice/EInvoicePreviewTab';
import EInvoiceQueueTab from '../components/eInvoice/EInvoiceQueueTab';
import EInvoiceRunStatusCard from '../components/eInvoice/EInvoiceRunStatusCard';
import EInvoiceSettingsTab from '../components/eInvoice/EInvoiceSettingsTab';
import { useAuth } from '../contexts/AuthContext';
import { E_INVOICE_CHANGED_EVENT, type EInvoiceSummary, isAdminRole } from '../domain/eInvoice';
import useEInvoiceRunStatus from '../hooks/useEInvoiceRunStatus';
import useQuery from '../hooks/useQuery';

const SUMMARY_POLL_MS = 60 * 1000;

/**
 * The E-Invoice module page (e-Factura through SmartBill; plan §7.2).
 *
 * Reached only through `ModuleGuard`, so the module is licensed and switched on. Everyone sees the
 * queue and the progress of the latest run (§6.10); the settings and the preview are the
 * administrator's (decision D8). A banner says so
 * whenever the installation only simulates SmartBill, which every installation except a shop's
 * production one does (owner rule: no live testing).
 */
export default function EInvoice() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAdmin = isAdminRole(user?.role);
    const { data, reload } = useQuery<{ data: EInvoiceSummary }>({
        path: '/e-invoice/summary',
        pollInterval: SUMMARY_POLL_MS,
    });
    const summary = data?.data;
    const reloadSummary = useCallback(() => {
        reload();
        globalThis.dispatchEvent(new Event(E_INVOICE_CHANGED_EVENT));
    }, [reload]);
    // Bumped when a run ends, so the queue shows what it sent.
    const [queueReloads, setQueueReloads] = useState(0);
    const runEndedHandler = useCallback(() => {
        reloadSummary();
        setQueueReloads(count => count + 1);
    }, [reloadSummary]);
    const runStatus = useEInvoiceRunStatus(runEndedHandler);

    const items = [
        {
            key: 'queue',
            label: t('eInvoice.tabs.queue'),
            children: (
                <EInvoiceQueueTab
                    isAdmin={isAdmin}
                    summary={summary}
                    onChanged={reloadSummary}
                    runActive={runStatus.active}
                    onRunStarted={runStatus.reload}
                    reloadKey={queueReloads}
                />
            ),
        },
        ...(isAdmin
            ? [
                  {
                      key: 'settings',
                      label: t('eInvoice.tabs.settings'),
                      children: <EInvoiceSettingsTab onChanged={reloadSummary} />,
                  },
                  {
                      key: 'preview',
                      label: t('eInvoice.tabs.preview'),
                      children: <EInvoicePreviewTab />,
                  },
              ]
            : []),
    ];

    return (
        <div data-testid="e-invoice-page">
            <PageHeader
                accent="#0e7490"
                icon={<FileDoneOutlined aria-hidden />}
                title={t('eInvoice.title')}
                subtitle={t('eInvoice.subtitle')}
            />
            {summary && !summary.sending.live && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    data-testid="e-invoice-simulation-banner"
                    message={t('eInvoice.sending.simulationTitle')}
                    description={t('eInvoice.sending.simulationDescription', {
                        scenario: summary.sending.simulationScenario ?? 'accept',
                    })}
                />
            )}
            <EInvoiceRunStatusCard run={runStatus.run} loaded={runStatus.loaded} />
            <Tabs items={items} destroyOnHidden />
        </div>
    );
}
